/**
 * <cc-gallery>: a scroll-snap image slider (swipe on touch), prev/next arrows,
 * a counter, thumbnails and arrow-key support. The active index follows the
 * scroll position so every input method stays in sync. Markup comes from
 * snippets/cc-gallery.liquid.
 */
class CCGallery extends HTMLElement {
  connectedCallback() {
    const track = this.querySelector('[data-ref="galTrack"]');
    const slides = track ? Array.from(track.querySelectorAll('.cc-gal__slide')) : [];
    this.setupDeferredSlides();
    if (track && slides.length) this.setupLightbox(track, slides);
    if (!track || slides.length < 2) return;
    const thumbs = Array.from(this.querySelectorAll('.cc-gal__thumb'));
    const thumbStrip = this.querySelector('.cc-gal__thumbs');
    const prev = this.querySelector('[data-ref="galPrev"]');
    const next = this.querySelector('[data-ref="galNext"]');
    const counter = this.querySelector('[data-ref="galCounter"]');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const n = slides.length;
    let index = 0;

    const setActive = (i) => {
      index = i;
      thumbs.forEach((t, k) => {
        const on = k === i;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', String(on));
        // Scroll the strip itself, never scrollIntoView (that would also scroll the page).
        if (on && thumbStrip) {
          const left = t.offsetLeft - (thumbStrip.clientWidth - t.clientWidth) / 2;
          thumbStrip.scrollTo({ left: Math.max(0, left), behavior: reduceMotion ? 'auto' : 'smooth' });
        }
      });
      if (counter) counter.textContent = `${i + 1} / ${n}`;
    };
    const go = (i, behavior) => {
      const target = ((i % n) + n) % n;
      track.scrollTo({ left: slides[target].offsetLeft, behavior: behavior || (reduceMotion ? 'auto' : 'smooth') });
      setActive(target);
    };
    this.goTo = go;

    let raf = 0;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const i = Math.round(track.scrollLeft / track.clientWidth);
        if (i !== index && i >= 0 && i < n) setActive(i);
      });
    }, { passive: true });
    prev?.addEventListener('click', () => go(index - 1));
    next?.addEventListener('click', () => go(index + 1));
    thumbs.forEach((t) => t.addEventListener('click', () => go(Number(t.dataset.index))));
    track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); go(index - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); go(index + 1); }
      if (e.key === 'Home') { e.preventDefault(); go(0); }
      if (e.key === 'End') { e.preventDefault(); go(n - 1); }
    });
    // Jump to a slide by media id (used when a variant with its own image is chosen).
    this.addEventListener('cc:gallery-show', (e) => {
      const i = slides.findIndex((s) => s.dataset.mediaId === String(e.detail?.mediaId));
      if (i >= 0) go(i);
    });
    if ('ResizeObserver' in window) new ResizeObserver(() => go(index, 'auto')).observe(track);
  }

  /**
   * On product pages the slides after the first are rendered without a source, so they can't
   * compete with the first photo (the page's LCP). They get their source back once the page has
   * loaded, or sooner on the first hover, touch or focus.
   */
  setupDeferredSlides() {
    const waiting = () => Array.from(this.querySelectorAll('img[data-srcset], img[data-src]'));
    if (!waiting().length) return;
    const hydrate = () => waiting().forEach((img) => {
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      if (img.dataset.src) img.src = img.dataset.src;
      img.removeAttribute('data-srcset');
      img.removeAttribute('data-src');
    });
    if (document.readyState === 'complete') hydrate();
    else window.addEventListener('load', hydrate, { once: true });
    ['pointerenter', 'touchstart', 'focusin'].forEach((type) => this.addEventListener(type, hydrate, { once: true, passive: true }));
  }

  /**
   * Tap or click a slide to see it full screen (a native <dialog>), with arrows, swipe,
   * arrow keys and Escape. A swipe on the slider itself never opens it: the pointer has to
   * go down and up within a few pixels.
   */
  setupLightbox(track, slides) {
    const box = this.querySelector('[data-ref="lightbox"]');
    const img = box?.querySelector('[data-ref="lbImg"]');
    if (!box || !img || typeof box.showModal !== 'function') return;
    const counter = box.querySelector('[data-ref="lbCounter"]');
    const n = slides.length;
    let current = 0;
    // Full-size files are fetched on the first sign of interest (hover, touch or focus), never on
    // page load: eight large files would compete with the first photo, which is the product
    // page's LCP. Until a full file has arrived, the slide's own (already loaded) image is shown
    // and swapped for the large one the moment it lands.
    const full = new Map();
    const fetchFull = (slide) => {
      const url = slide.dataset.full;
      if (!url || full.has(url)) return full.get(url);
      const pre = new Image();
      pre.decoding = 'async';
      const done = new Promise((resolve) => { pre.onload = () => resolve(url); pre.onerror = () => resolve(null); });
      pre.src = url;
      full.set(url, done);
      return done;
    };
    const warm = () => slides.forEach(fetchFull);
    ['pointerenter', 'touchstart', 'focusin'].forEach((type) => this.addEventListener(type, warm, { once: true, passive: true }));
    let token = 0;
    const show = (i) => {
      current = ((i % n) + n) % n;
      const slide = slides[current];
      const source = slide.querySelector('img');
      const mine = ++token;
      img.src = source?.currentSrc || source?.src || '';
      img.alt = source?.alt || '';
      const ready = fetchFull(slide);
      if (ready) ready.then((url) => { if (url && mine === token) img.src = url; });
      if (counter) counter.textContent = n > 1 ? `${current + 1} / ${n}` : '';
      if (typeof this.goTo === 'function') this.goTo(current, 'auto');
    };
    const open = (i) => { show(i); box.showModal(); box.querySelector('[data-ref="lbClose"]')?.focus(); };
    let down = null;
    track.addEventListener('pointerdown', (e) => { down = { x: e.clientX, y: e.clientY, t: e.target.closest('.cc-gal__slide') }; });
    track.addEventListener('pointerup', (e) => {
      if (!down || !down.t) return;
      const moved = Math.hypot(e.clientX - down.x, e.clientY - down.y);
      const target = down.t;
      down = null;
      if (moved < 8) open(slides.indexOf(target));
    });
    track.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); open(Math.round(track.scrollLeft / track.clientWidth)); } });
    box.querySelector('[data-ref="lbClose"]')?.addEventListener('click', () => box.close());
    box.querySelector('[data-ref="lbPrev"]')?.addEventListener('click', () => show(current - 1));
    box.querySelector('[data-ref="lbNext"]')?.addEventListener('click', () => show(current + 1));
    box.addEventListener('click', (e) => { if (e.target === box || e.target === img) box.close(); });
    box.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); show(current - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); show(current + 1); }
    });
    let swipe = null;
    box.addEventListener('pointerdown', (e) => { swipe = e.clientX; });
    box.addEventListener('pointerup', (e) => {
      if (swipe === null || n < 2) return;
      const dx = e.clientX - swipe;
      swipe = null;
      if (Math.abs(dx) > 40) { show(current + (dx < 0 ? 1 : -1)); e.stopPropagation(); }
    });
  }
}

if (!customElements.get('cc-gallery')) customElements.define('cc-gallery', CCGallery);
